import { useEffect, useState } from 'react';
import { Inbox } from 'lucide-react';
import {
  decideMuqriRequest, listenToDecidedRequests, listenToPendingRequests, type MuqriRequest,
} from '@core/firebase/admin';
import { useLanguage } from '@/contexts/LanguageContext';
import { ConfirmButton } from '@/components/ConfirmButton';
import {
  EmptyState, LimePill, Notice, PageHeader, Segmented, SoftCard,
} from '@/components/PageKit';

type Filter = 'pending' | 'approved' | 'rejected';

/**
 * Who asked to teach, and the decision.
 *
 * Live rather than fetched once: two admins can be looking at the same list,
 * and a decided row should leave rather than sit there inviting a second
 * decision. Deciding is a callable — the rules refuse an admin every direct
 * write to roles or to a request's status — so it can be refused, and a
 * refusal has to be visible.
 *
 * Redesign 2026-10 (board a1): a segmented filter — «جديدة» with its count,
 * «مقبولة», «مرفوضة» — and each request a soft card in a grid, «اقبل» filled
 * and «ارفض» a red outline, both asking first. Decided requests are read
 * only: a decision is never re-made from here.
 */
export default function RequestsPage() {
  const { t } = useLanguage();
  const [filter, setFilter] = useState<Filter>('pending');
  const [requests, setRequests] = useState<MuqriRequest[]>([]);
  const [decided, setDecided] = useState<MuqriRequest[]>([]);
  const [errorKey, setErrorKey] = useState<string | null>(null);

  useEffect(
    () => listenToPendingRequests(setRequests, () => setErrorKey('admin.requests.failed')),
    []
  );
  useEffect(() => {
    if (filter === 'pending') { setDecided([]); return undefined; }
    return listenToDecidedRequests(filter, setDecided, () => setErrorKey('admin.requests.failed'));
  }, [filter]);

  const decide = (uid: string, decision: 'approved' | 'rejected') => {
    setErrorKey(null);
    // Nothing is removed locally: the listener queries status == 'pending',
    // so the row leaves when the SERVER says it left.
    void decideMuqriRequest(uid, decision).catch(() => setErrorKey('admin.requests.failed'));
  };

  const count = (n: number) => new Intl.NumberFormat('en-US').format(n);
  const shown = filter === 'pending' ? requests : decided;

  return (
    <div className="mx-auto w-full max-w-6xl space-y-6">
      <PageHeader
        title={t('admin.requests.title')}
        subtitle={t('admin.requests.subtitle')}
        actions={(
          <Segmented<Filter>
            label={t('admin.requests.filterLabel')}
            value={filter}
            onChange={setFilter}
            options={[
              {
                value: 'pending',
                testId: 'filter-pending',
                label: (
                  <>
                    {t('admin.requests.filterPending')}
                    {requests.length > 0 && (
                      <span className="rounded-full bg-lime px-1.5 text-xs font-semibold text-accent-foreground">
                        {count(requests.length)}
                      </span>
                    )}
                  </>
                ),
              },
              { value: 'approved', testId: 'filter-approved', label: t('admin.requests.filterApproved') },
              { value: 'rejected', testId: 'filter-rejected', label: t('admin.requests.filterRejected') },
            ]}
          />
        )}
      />

      {errorKey && <Notice tone="danger" testId="requests-error">{t(errorKey)}</Notice>}

      {shown.length === 0 ? (
        <SoftCard as="div">
          <EmptyState
            testId="no-requests"
            icon={<Inbox />}
            title={t(filter === 'pending' ? 'admin.requests.emptyTitle' : 'admin.requests.emptyDecided')}
            body={filter === 'pending' ? t('admin.requests.emptyBody') : undefined}
          />
        </SoftCard>
      ) : (
        <ul className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {shown.map((r) => {
            const name = r.displayName || r.uid;
            return (
              <li
                key={r.uid}
                data-testid={`request-${r.uid}`}
                className="card-soft flex flex-col gap-4 p-5"
              >
                <div className="flex items-start justify-between gap-3">
                  <p className="min-w-0 truncate text-[17px] font-semibold text-ink">{name}</p>
                  {filter === 'approved' && <LimePill>{t('admin.requests.filterApproved')}</LimePill>}
                  {filter === 'rejected' && (
                    <span className="shrink-0 rounded-full bg-danger/10 px-3 py-1 text-xs font-semibold text-danger">
                      {t('admin.requests.filterRejected')}
                    </span>
                  )}
                </div>
                {r.note && <p className="line-clamp-4 text-sm text-helper">{r.note}</p>}
                {filter === 'pending' && (
                  <div className="mt-auto flex flex-wrap items-center gap-2">
                    <ConfirmButton
                      testId={`approve-${r.uid}`}
                      tone="primary"
                      label={t('admin.requests.approve')}
                      question={t('admin.requests.confirmApprove', { name })}
                      onConfirm={() => decide(r.uid, 'approved')}
                    />
                    {/* Reject is the red OUTLINE, never the row's filled
                        action: a destructive default is how the wrong person
                        gets rejected. */}
                    <ConfirmButton
                      testId={`reject-${r.uid}`}
                      tone="danger"
                      label={t('admin.requests.reject')}
                      question={t('admin.requests.confirmReject', { name })}
                      onConfirm={() => decide(r.uid, 'rejected')}
                    />
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
